import 'katex/dist/katex.min.css';

import { ErrorInfo, useEffect, useRef } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import ReactMarkdown, { Components, Options } from 'react-markdown';
import rehypeKatex from 'rehype-katex';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import { z } from 'zod';

import { CustomBlockquote } from './custom-components/CustomBlockquote';
import { CustomCode, INLINE_CODE } from './custom-components/CustomCode';
import {
  CustomLink,
  CustomTable,
  CustomTableCell,
  CustomTableHeader,
} from './custom-components/CustomComponents';
import styles from './Markdown.module.scss';

export const MARKDOWN_MAX_LENGTH = 100_000;

export const markdownPropsSchema = z
  .object({
    message: z.string().min(1).max(MARKDOWN_MAX_LENGTH),
    isDark: z.boolean().optional(),
  })
  .strict();

export type MarkdownDataProps = z.infer<typeof markdownPropsSchema>;

export interface MarkdownProps extends MarkdownDataProps {
  /** Raw markdown source. Underscored pipes (`|_`) are auto-replaced with `| ` to work around a GPT-table quirk. */
  message: string;
  /** Called once the rendered markdown is mounted/updated, with the wrapper `<div>`. Useful for measuring or scrolling into view. */
  onLoad?: (element: HTMLDivElement) => void;
  /** Override individual ReactMarkdown component renderers (e.g. custom `img`, `pre`). Merged on top of the design-system defaults. */
  components?: Partial<Components>;
  /** Extra remark plugins, run after the design-system defaults (GFM + math). */
  remarkPlugins?: Options['remarkPlugins'];
  /** Called by the surrounding `ErrorBoundary` when rendering throws (e.g. malformed math). */
  onError?: (error: unknown, info: ErrorInfo) => void;
  /** Theme preset for code-block syntax highlighting. Defaults to `true` (dark theme). */
  isDark?: boolean;
}

/**
 * Markdown renderer used by Alfred chat output.
 *
 * Built on `react-markdown` with GitHub-flavored markdown
 * (`remark-gfm`) and KaTeX math (`remark-math` + `rehype-katex`)
 * enabled. Ships design-system-themed renderers for tables, links,
 * code blocks (with syntax highlighting), and blockquotes; consumers
 * can override any node renderer via `components`. Wrapped in an
 * `ErrorBoundary` so a single broken token can't crash the surrounding
 * view.
 */
export const Markdown = ({
  message,
  onLoad,
  components,
  remarkPlugins,
  onError,
  isDark = true,
}: MarkdownProps) => {
  // This is fix for table markdown. Need to check if BE/DS can fix the GPT response to not include underscore with tables
  // Discussion: https://toggleglobal.slack.com/archives/C053NT28Q8M/p1686422095030189
  const formattedMarkdown = message.replace(/\|_/g, '| ');

  const markdownRef = useRef<HTMLDivElement>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: onLoad should run after markdown content changes.
  useEffect(() => {
    if (onLoad && markdownRef.current) {
      onLoad(markdownRef.current);
    }
  }, [message]);

  return (
    <ErrorBoundary fallback={null} onError={onError}>
      <div
        ref={markdownRef}
        className={styles.markdownStyles}
        data-markdown
        data-testid="markdown"
      >
        <ReactMarkdown
          remarkPlugins={[
            [remarkGfm, { singleTilde: false }],
            [remarkMath, { singleDollarTextMath: false }],
            ...(remarkPlugins ?? []),
          ]}
          rehypePlugins={[rehypeKatex]}
          components={{
            a: CustomLink,
            td: CustomTableCell,
            th: CustomTableHeader,
            table: CustomTable,
            code: ({ children, className = INLINE_CODE }) => {
              return (
                <CustomCode isDark={isDark} language={className}>
                  {children}
                </CustomCode>
              );
            },
            blockquote: CustomBlockquote,
            ...components,
          }}
        >
          {formattedMarkdown}
        </ReactMarkdown>
      </div>
    </ErrorBoundary>
  );
};
